import * as Sentry from '@sentry/react';

import { IS_SERVER } from '@/renderer/utils/ssr';
import { AuthRefreshResult, authToken, refreshToken } from '@/renderer/utils/useMe';
import type { JSONValue } from '@/types/json';

import { MSG_SUBSCRIBE, MSG_SUBSCRIBE_REGEX, MSG_UNSUBSCRIBE, msg } from './message';
import type { ConnectionPool } from './types';
import { ReadyState } from './types';

// Shared connection pool by URL
const connectionPool: ConnectionPool = {};

// Max reconnect delay for exponential backoff
const MAX_DELAY = 25000;
const MIN_DELAY = 1000;
const JITTER = 1000;

const UP_STATES = [
  ReadyState.UNINITIALIZED,
  ReadyState.CONNECTING,
  ReadyState.OPEN,
  ReadyState.READY,
];

export function wsPad<const S extends string>(str: S) {
  return !/^wss?:\/\//i.test(str)
    ? `${`${(IS_SERVER ? import.meta.env.VITE_PRIVATE_ENV__API_URL : '') || import.meta.env.VITE_PUBLIC_ENV__API_URL || (typeof location !== 'undefined' ? location.origin : import.meta.env.DEV ? 'http://localhost:3000' : '')}`.replace('http', 'ws')}/${
        str[0] === '/' ? str.slice(1) : str
      }`
    : str;
}

const createConnection = (url: string, connect = true) => {
  const socket = connect ? new WebSocket(url) : null;

  // biome-ignore lint/suspicious/noAssignInExpressions: This is not great but it works for now
  const connection: ConnectionPool[string] = (connectionPool[url] = {
    socket,
    readyState: connect ? ReadyState.CONNECTING : ReadyState.UNINITIALIZED,
    queue: [],
    baseListeners: new Map(),
    listeners: new Map(),
    subscribers: new Map(),
    readyStaters: new Set(),
    timer: new Map(),
    delay: MIN_DELAY + Math.round(Math.random() * JITTER),
    shouldReconnect: true,
    mounted: new Set(),
    resubscribe: () => {
      // But compare these to what's already in the queue so we don't double-send
      const queued = new Set(connection.queue);

      for (const [sub, subscriber] of connection.subscribers.entries()) {
        if (subscriber?.size) {
          const nextmsg = msg(MSG_SUBSCRIBE, sub);

          if (!queued.has(nextmsg)) {
            connection.sendMessage(nextmsg);
            queued.add(nextmsg);
          }
        }
      }
    },
    close: () => {
      connection.shouldReconnect = false;
      connection.socket?.close();
    },
    reconnect: () => {
      connection.shouldReconnect = true;
      reconnectConnection(url);
    },
    setReadyState: (next) => {
      if (next === ReadyState.READY) {
        if (connection.readyState !== ReadyState.READY) {
          // If we switched from non-ready to ready, we should iterate over the subscribers and send them in
          connection.resubscribe();
        }

        // Then send whatever is left in the queue
        flushQueue();
      } else if (next === ReadyState.DISCONNECTING || next === ReadyState.CLOSED) {
        // Clear the queue when closing the connection, so that we don't send them next time
        connection.queue.length = 0;
      }

      connection.readyState = next;

      for (const setReadyState of connection.readyStaters) {
        try {
          setReadyState(next);
        } catch (error) {
          Sentry.captureException(error);
        }
      }
    },
    unlisten: (handlers, subscriptions) => {
      if (subscriptions) {
        if (!connection.timer.has('subscriptions')) {
          connection.timer.set('subscriptions', new Map());
        }

        const subtimers = connection.timer.get('subscriptions') as unknown as Map<
          any,
          Map<any, NodeJS.Timeout>
        >;

        for (const sub in subscriptions) {
          if (!subtimers.has(sub)) {
            subtimers.set(sub, new Map());
          }

          const subtimermap = subtimers.get(sub);

          const fn = subscriptions[sub];

          const eventFnSet = connection.subscribers.get(sub);

          eventFnSet?.delete(fn as any);

          subtimermap!.set(
            fn,
            setTimeout(() => {
              if (eventFnSet?.size === 0) {
                connection.subscribers.delete(sub);
                if (connection.socket?.readyState === WebSocket.OPEN) {
                  connection.socket?.send(msg(MSG_UNSUBSCRIBE, sub));
                }
              }
            }, 50),
          );
        }
      }

      for (const ke in handlers) {
        const key = ke as keyof typeof handlers;
        const fn = handlers[key];

        if (fn && connection.listeners.has(key)) {
          const map = connection.listeners.get(key);

          map!.delete(fn);

          if (map!.size === 0) {
            connection.listeners.delete(key);
          }
        }
      }
    },
    unmount: (id, handlers, subscriptions, setReadyState) => {
      connection.mounted.delete(id);
      connection.readyStaters.delete(setReadyState);

      connection.unlisten(handlers, subscriptions);

      if (connection.mounted.size === 0 || connection.listeners.size === 0) {
        // If there are no more mounted instances, close and delete the connection
        connection.timer.set(
          '_close',
          setTimeout(() => {
            if (
              connection.mounted.size === 0 ||
              (connection.listeners.size === 0 && connection.subscribers.size === 0)
            ) {
              deleteConnection(url);
            }
          }, 2000),
        );
      }
    },
    relisten: (handlers, subscriptions) => {
      const subtimers = connection.timer.get('subscriptions') as unknown as Map<
        any,
        Map<any, NodeJS.Timeout>
      >;

      if (subscriptions) {
        for (const sub in subscriptions) {
          clearTimeout(subtimers?.get(sub)?.get(subscriptions[sub]));
        }
      }

      for (const ke in handlers) {
        const key = ke as keyof typeof handlers;
        const fn = handlers[key];

        if (fn) {
          if (!connection.listeners.has(key)) {
            connection.listeners.set(key, new Set());
          }

          connection.listeners.get(key)!.add(fn);
        }
      }

      for (const sub in subscriptions) {
        if (!connection.subscribers.has(sub)) {
          connection.subscribers.set(sub, new Set());
          connection.sendMessage(msg(MSG_SUBSCRIBE, sub));
        }

        connection.subscribers.get(sub)!.add(subscriptions[sub] as any);
      }
    },
    mount: (id, handlers, subscriptions, setReadyState) => {
      connection.mounted.add(id);

      if (connection.timer.has('_close')) {
        clearTimeout(connection.timer.get('_close'));
        connection.timer.delete('_close');
      }

      clearTimeout(connection.timer.get(handlers.open));
      clearTimeout(connection.timer.get(handlers.close));
      clearTimeout(connection.timer.get(handlers.error));
      clearTimeout(connection.timer.get(handlers.message));

      connection.relisten(handlers, subscriptions);

      if (setReadyState) {
        connection.readyStaters.add(setReadyState);
        connection.setReadyState(connection.readyState);
      }
    },
    sendMessage: (message: string, keep = true) => {
      if (
        connection.readyState === ReadyState.READY &&
        connection.socket?.readyState === WebSocket.OPEN
      ) {
        // If the readyState is READY, send the message immediately
        connection.socket.send(message);
      } else if (keep && UP_STATES.includes(connection.readyState)) {
        // If the readyState is UNINITIALIZED, CONNECTING, or OPEN, and keep is true, queue the message
        connection.queue.push(message);
      } else {
        console.warn('Cannot send message to closed socket');
      }
    },
  });

  function flushQueue() {
    const batch = [];
    let batchType: undefined | 'un' = undefined;

    while (
      connection.queue.length > 0 &&
      connection.socket?.readyState === WebSocket.OPEN &&
      connection.readyState === ReadyState.READY
    ) {
      const message = connection.queue.shift();

      if (message) {
        const match = message.match(MSG_SUBSCRIBE_REGEX);

        // Batch queued sub/unsub messages in order to reduce # of messages
        if (match) {
          if (batchType !== match[1] && batch.length) {
            connection.socket.send(
              msg(batchType === 'un' ? MSG_UNSUBSCRIBE : MSG_SUBSCRIBE, batch.join(',')),
            );
            batch.length = 0;
          }

          batchType = match[1] as undefined | 'un';
          batch.push(match[2]);
        } else {
          if (batch.length) {
            connection.socket.send(
              msg(batchType === 'un' ? MSG_UNSUBSCRIBE : MSG_SUBSCRIBE, batch.join(',')),
            );
            batch.length = 0;
          }

          connection.socket.send(message);
        }
      }
    }

    if (
      connection.socket?.readyState === WebSocket.OPEN &&
      connection.readyState === ReadyState.READY &&
      batch.length
    ) {
      connection.socket.send(
        msg(batchType === 'un' ? MSG_UNSUBSCRIBE : MSG_SUBSCRIBE, batch.join(',')),
      );
      batch.length = 0;
    }
  }

  // These will allow us to update react state from outside React
  const handleMessage = (event: MessageEvent) => {
    if (event.data?.startsWith?.('{"message":"AUTHORIZED"')) {
      // If data.message is AUTHORIZED, it is now ready to receive data from us
      connection.setReadyState(ReadyState.READY);
      connection.resubscribe();

      flushQueue();

      const fns = connection.listeners.get('ready');

      if (fns) {
        for (const fn of fns) {
          try {
            fn.call(connection, event);
          } catch (error) {
            Sentry.captureException(error);
          }
        }
      }
    } else if (event.data.startsWith('{"message":"UNAUTHORIZED"')) {
      // not allowed; clear queue and disconnect
      authToken(null);
    } else if (event.data.startsWith('{"message":"EXPIRED"')) {
      // refresh token and reconnect
      connection.close();
      console.log('gonna attempt to recon from expired');
      refreshToken().then(({ status }) => {
        if (status === AuthRefreshResult.SKIPPED || status === AuthRefreshResult.SUCCESS) {
          connection.reconnect();
        } else {
          // dispatch alert
          console.log('@todo');
        }
      });
      // } else if (event.data === 'PING' || event.data === 'PONG') {
      //   // If the data is PING or PONG, do nothing
      //   return;
    } else {
      let data: API.SubscriptionResponse<JSONValue, string> | null = null;

      try {
        if (event.data !== 'PING' && event.data !== 'PONG') {
          data = JSON.parse(event.data);
        }
      } catch (error) {
        console.warn(error);
      }

      try {
        // Check if there is a subscription for the message name and invoke it if any
        if (data?.message) {
          const fns = connection.subscribers.get(data.message);

          if (fns) {
            for (const fn of fns) {
              try {
                fn?.call(connection, event, data);
              } catch (error) {
                Sentry.captureException(error);
              }
            }
          }
        }

        {
          const fns = connection.listeners.get('message');

          if (fns) {
            for (const fn of fns) {
              setTimeout(() => {
                try {
                  fn?.call(connection, event, data);
                } catch (error) {
                  Sentry.captureException(error);
                }
              }, 0);
            }
          }
        }
      } catch (error) {
        console.error(error);

        const fns = connection.listeners.get('error');

        if (fns) {
          for (const fn of fns) {
            try {
              fn?.call(connection, error as Error);
            } catch (error2) {
              Sentry.captureException(error2);
            }
          }
        }
      }
    }
  };

  const handleOpen = (event: Event) => {
    // Set the readyState to OPEN and reset the delay
    connection.setReadyState(ReadyState.OPEN);

    connection!.delay = MIN_DELAY + Math.round(Math.random() * JITTER);

    const fns = connection.listeners.get('open');

    if (fns) {
      for (const fn of fns) {
        try {
          fn?.call(connection, event);
        } catch (error) {
          Sentry.captureException(error);
        }
      }
    }
  };

  const handleClose = (event: CloseEvent) => {
    // Set the readyState to CLOSED and reconnect with exponential backoff
    connection.setReadyState(ReadyState.CLOSED);

    console.log(
      'handle close',
      connect,
      connection?.shouldReconnect,
      connectionPool[url]?.shouldReconnect,
    );
    reconnectConnection(url);

    const fns = connection.listeners.get('close');

    if (fns) {
      for (const fn of fns) {
        try {
          fn?.call(connection, event);
        } catch (error) {
          Sentry.captureException(error);
        }
      }
    }
  };

  const handleError = (event: Event) => {
    // Set the readyState to CLOSED and reconnect with exponential backoff
    connection.setReadyState(ReadyState.CLOSED);

    console.log('handle error', connect, connection.shouldReconnect);
    reconnectConnection(url);

    const fns = connection.listeners.get('error');

    if (fns) {
      for (const fn of fns) {
        try {
          fn?.call(connection, event);
        } catch (error) {
          Sentry.captureException(error);
        }
      }
    }
  };

  connection.socket?.addEventListener('message', handleMessage);
  connection.socket?.addEventListener('open', handleOpen);
  connection.socket?.addEventListener('close', handleClose);
  connection.socket?.addEventListener('error', handleError);

  connection.baseListeners.set('message', handleMessage);
  connection.baseListeners.set('open', handleOpen);
  connection.baseListeners.set('close', handleClose);
  connection.baseListeners.set('error', handleError);

  return connection;
};

// Get or create a connection from the pool
export const getConnection = (url: string, connect = true) => {
  // If there is an existing connection for the url, return it
  if (connectionPool[url]) {
    if (connect) {
      reconnectConnection(url);
    }

    return connectionPool[url];
  }

  // Otherwise, create a new connection and store it in the pool
  return createConnection(url, connect);
};

// Close and delete a connection from the pool
const deleteConnection = (url: string) => {
  if (!connectionPool[url]) {
    return;
  }

  // Get the connection from the pool
  const { socket, timer } = connectionPool[url];

  socket?.close();
  if (timer.has('_retry')) {
    clearTimeout(timer.get('_retry'));
    timer.delete('_retry');
  }

  // Delete the connection from the pool
  delete connectionPool[url];
};

// Reconnect with exponential backoff
const reconnectConnection = (url: string) => {
  if (!connectionPool[url]) {
    return;
  }

  // Get the connection from the pool
  const { socket, timer, delay } = connectionPool[url];

  // If the socket is not closed, do nothing
  if (socket && socket.readyState !== WebSocket.CLOSED && socket.readyState !== WebSocket.CLOSING) {
    return;
  }

  if (timer.has('_close')) {
    clearTimeout(timer.get('_close'));
    timer.delete('_close');
  }

  if (timer.has('_retry')) {
    return;
  }

  // Create a new timer to reconnect after some delay
  timer.set(
    '_retry',
    setTimeout(() => {
      if (connectionPool[url].shouldReconnect) {
        try {
          // Create a new socket with the same url and assign it to the connection
          const newSocket = new WebSocket(url);

          connectionPool[url].socket = newSocket;

          if (connectionPool[url].timer.has('_retry')) {
            clearTimeout(connectionPool[url].timer.get('_retry'));
            connectionPool[url].timer.delete('_retry');
          }

          // Add all the existing listeners to the new socket
          for (const [name, fn] of connectionPool[url].baseListeners.entries()) {
            newSocket.addEventListener(name, fn);
          }

          // Set the readyState to CONNECTING and reset the queue
          connectionPool[url].setReadyState(ReadyState.CONNECTING);
        } catch (error) {
          console.warn(error);

          if (connectionPool[url].timer.has('_retry')) {
            clearTimeout(connectionPool[url].timer.get('_retry'));
            connectionPool[url].timer.delete('_retry');
          }

          reconnectConnection(url);
        }
      }

      // Double the delay and cap it at the maximum value
      const newDelay = Math.min(
        delay * 2 + Math.round(Math.random() * JITTER),
        MAX_DELAY + Math.round(Math.random() * JITTER),
      );

      connectionPool[url].delay = newDelay;
    }, delay),
  );
};
